/**
 * Reparto por proximidad a hoy: atrasadas / hoy / esta semana / próxima semana /
 * más adelante / sin fecha.
 *
 * Es lo que da sentido a la pantalla de cargas de salida: con ~660 cargas vivas,
 * una lista ordenada por fecha no dice si algo va tarde; una banda que pone
 * «ATRASADAS · 167 cargas» sí.
 *
 * Dos cosas que esta función NO hace, y son deliberadas:
 *
 * - **No descarta nada.** El horizonte de treinta días es del `fetch`, no de
 *   aquí. Si el backend mandara algo a cuarenta y cinco días, entra en
 *   `masAdelante` y se ve. Una función de agrupar que además esconde filas es
 *   una función que pierde datos en silencio.
 * - **No consulta el reloj.** `hoy` es parámetro, para que el test no dependa de
 *   la fecha de quien lo ejecute — que es exactamente cómo se pudre un test.
 */
import type { BucketFecha } from '@/types'

/** El orden en el que se pintan las bandas. `sinFecha` va siempre al final,
 *  aunque tenga más elementos que ninguna. */
const ORDEN: BucketFecha[] = [
  'atrasadas', 'hoy', 'estaSemana', 'proximaSemana', 'masAdelante', 'sinFecha',
]

export interface GrupoFecha<T> {
  bucket: BucketFecha
  items: T[]
  /** Primer y último día cubiertos, para el rótulo «ESTA SEMANA · 22–25 SEP».
   *  `null` en `sinFecha`, que por definición no cubre ningún día. */
  desde: Date | null
  hasta: Date | null
  /** Sólo en `atrasadas`: el retraso de la más antigua, en días. */
  diasRetrasoMax?: number
}

/** El mismo día, a las 00:00 en hora local. */
function dia(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

/**
 * Días de calendario entre dos fechas.
 *
 * No es `(b - a) / MS_DIA`: en la semana del cambio de hora esa división da
 * 44,96 y trunca a 44, así que un retraso de 45 días se enseña como de 44. Se
 * cuenta sobre medianoches en UTC, que es la forma de que la aritmética no la
 * toque el horario de verano.
 */
export function diasRetraso(fecha: Date, hoy: Date): number {
  const a = Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate())
  const b = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  return Math.round((b - a) / 86_400_000)
}

/** El lunes de la semana de `d`, a las 00:00 locales.
 *
 *  No se usa `getWeekStart` de `planningDateRanges` a propósito: aquella consulta
 *  `new Date()` por su cuenta y desharía el único motivo por el que `hoy` es
 *  parámetro. La semana se parte por el mismo sitio —lunes— que en el Gantt. */
function lunesDe(d: Date, semanas = 0): Date {
  const x = dia(d)
  const dow = x.getDay()
  x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow) + semanas * 7)
  return x
}

function bucketDe(fecha: Date | null, hoy: Date): BucketFecha {
  if (!fecha) return 'sinFecha'
  const f = dia(fecha)
  const h = dia(hoy)
  if (f < h) return 'atrasadas'
  if (f.getTime() === h.getTime()) return 'hoy'

  const lunesQueViene = lunesDe(hoy, 1)
  const lunesSiguiente = lunesDe(hoy, 2)
  // `hoy` ya se ha descartado arriba, así que «esta semana» es todo lo que queda
  // de la semana en curso: de mañana al domingo.
  if (f < lunesQueViene) return 'estaSemana'
  if (f < lunesSiguiente) return 'proximaSemana'
  return 'masAdelante'
}

/**
 * Agrupa por proximidad a `hoy`.
 *
 * Se compara por **día local**, truncando la hora: una carga fechada hoy a las
 * 22:00 está en `hoy`, no en `masAdelante`.
 *
 * Dentro de cada banda, las cargas van de más antigua a más nueva — y en
 * `atrasadas` eso pone arriba la que lleva más tiempo parada, que es la que hay
 * que mirar. Los buckets vacíos NO se devuelven: una banda que dice «0 cargas»
 * es ruido en una pantalla que ya tiene seis.
 *
 * **Invariante**: `grupos.flatMap(g => g.items).length === items.length`. Nada se
 * pierde y nada se duplica.
 */
export function agruparPorFecha<T>(
  items: T[],
  fechaDe: (item: T) => Date | null,
  opciones?: { hoy?: Date; clave?: (item: T) => string },
): GrupoFecha<T>[] {
  const hoy = opciones?.hoy ?? new Date()
  const clave = opciones?.clave

  const cajones = new Map<BucketFecha, T[]>()
  for (const item of items) {
    const b = bucketDe(fechaDe(item), hoy)
    const lista = cajones.get(b)
    if (lista) lista.push(item)
    else cajones.set(b, [item])
  }

  const grupos: GrupoFecha<T>[] = []
  for (const bucket of ORDEN) {
    const lista = cajones.get(bucket)
    if (!lista || lista.length === 0) continue

    lista.sort((a, b) => {
      const fa = fechaDe(a)
      const fb = fechaDe(b)
      const ta = fa ? dia(fa).getTime() : 0
      const tb = fb ? dia(fb).getTime() : 0
      if (ta !== tb) return ta - tb
      return clave ? clave(a).localeCompare(clave(b)) : 0
    })

    const fechas = lista.map(fechaDe).filter((f): f is Date => f !== null).map(dia)
    const desde = fechas.length ? fechas[0] : null
    const hasta = fechas.length ? fechas[fechas.length - 1] : null

    grupos.push({
      bucket,
      items: lista,
      desde,
      hasta,
      ...(bucket === 'atrasadas' && desde ? { diasRetrasoMax: diasRetraso(desde, hoy) } : {}),
    })
  }
  return grupos
}

/**
 * Recuento por bucket **incluidos los vacíos**.
 *
 * `agruparPorFecha` omite los vacíos porque la pantalla no debe pintar una banda
 * con cero dentro; el KPI sí necesita ese cero, porque «0 atrasadas» es una
 * noticia y una casilla en blanco no dice nada.
 */
export function contarPorBucket<T>(grupos: GrupoFecha<T>[]): Record<BucketFecha, number> {
  const base = Object.fromEntries(ORDEN.map((b) => [b, 0])) as Record<BucketFecha, number>
  for (const g of grupos) base[g.bucket] = g.items.length
  return base
}
